import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as SPORTS_ICON } from "../../../assets/images/Frame (10).svg";
import { ReactComponent as SLOTS_ICON } from "../../../assets/images/Slots.svg";
import { ReactComponent as EVENT_ICON } from "../../../assets/images/star-icon.svg";
import { ReactComponent as HOLDEM_ICON } from "../../../assets/images/Frame (29).svg";
import { CasinoCardIcon as CASINO_ICON } from "../../Common/Icons/GameCategoryIcons";
import { useAuth } from "../../../context/AuthContext";
import config from "../../../lib/config";
import { siteApi } from "../../../lib/api";
import { launchHoldemGame, launchProviderLobby } from "../../../lib/gameLaunch";
import {
  createFavoriteProviderItem,
  FAVORITES_UPDATED_EVENT,
  getFavoriteGames,
  toggleFavoriteGame,
} from "../../../utils/favoriteGames";
import { getRecentPlayAccountKey } from "../../../utils/recentPlayHistory";
import { getIntegratedSportsbookRoute } from "../../../utils/gameCatalog";
import { getBoardMainImageCandidates } from "../../../utils/boardContent";
import FavoriteToggleButton from "./FavoriteToggleButton";
import { StyledHomeBackendSections } from "./StyledHomeBackendSections";
import useContinuousHorizontalScroll from "./useContinuousHorizontalScroll";

const VENDOR_SECTIONS = [
  {
    id: "sports",
    title: "스포츠",
    gameType: "live-sports",
    icon: SPORTS_ICON,
    sectionUrl: "/sports?type=DOMESTIC",
    launchMode: "providerLobby",
    getCardUrl: getIntegratedSportsbookRoute,
  },
  {
    id: "casino",
    title: "카지노게임",
    gameType: "live-casino",
    icon: CASINO_ICON,
    sectionUrl: "/casino-games?tab=casino",
    launchMode: "providerLobby",
    getCardUrl: () => null,
  },
  {
    id: "slots",
    title: "슬롯게임",
    gameType: "slots",
    icon: SLOTS_ICON,
    sectionUrl: "/casino-games?tab=slots",
    getCardUrl: (vendor) => `/slots?vendor=${encodeURIComponent(vendor.vendor_name || "")}`,
  },
  {
    id: "holdem",
    title: "홀덤",
    gameType: "holdem",
    icon: HOLDEM_ICON,
    sectionUrl: null,
    launchMode: "holdem",
    getCardUrl: () => null,
  },
];

const EVENT_SECTION = {
  id: "events",
  title: "이벤트",
  icon: EVENT_ICON,
  sectionUrl: "/event",
};

const trimLeadingSlash = (value = "") => value.replace(/^\/+/, "");

const resolveAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `${config.backendUrl}/${trimLeadingSlash(trimmed)}`;
};

const getRows = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const normalizeKoreanTitle = (value = "") =>
  value
    .replace(/\bBTI\b/gi, "비티아이")
    .replace(/\bBT1\b/gi, "비티원")
    .replace(/\bSABA\b/gi, "사바")
    .replace(/\bTITI\b/gi, "티티")
    .replace(/\bSA(?=게이밍|카지노|스포츠|\s|$)/gi, "에스에이")
    .replace(/\s+/g, " ")
    .trim();

const getVendorTitle = (vendor) =>
  normalizeKoreanTitle(vendor?.vendor_name_kr || vendor?.vendor_name_ko || vendor?.vendor_name || "게임 벤더");

const uniqueUrls = (urls) => Array.from(new Set(urls.filter(Boolean)));

const getVendorImages = (vendor) =>
  uniqueUrls([
    resolveAssetUrl(vendor?.image),
    resolveAssetUrl(vendor?.pc_image),
    resolveAssetUrl(vendor?.mobile_image),
    resolveAssetUrl(vendor?.thumbnail),
    resolveAssetUrl(vendor?.vendor_list?.thumbnails),
  ]);

const dispatchAuthRequired = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("crypto-auth-required", {
      detail: { modalOption: "login", reason: "favorite" },
    })
  );
};

const stripHtml = (value = "") => value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const normalizeEventCard = (event) => {
  const images = getBoardMainImageCandidates(event);

  return {
    id: event?.id,
    title: stripHtml(event?.title) || event?.category || "이벤트",
    image: images[0] || "",
    images,
    href: "/event",
    meta: event?.category || "EVENT",
  };
};

export const SectionRail = ({
  section,
  items,
  isLoading = false,
  skeletonCount = 6,
  renderCard,
  renderEmpty,
  emptyText,
  errorText,
  autoScroll = false,
}) => {
  const railRef = useRef(null);
  const autoScrollCopyCount =
    autoScroll && items.length > 1 ? Math.max(3, Math.ceil(8 / items.length) + 2) : 1;
  const railItems =
    autoScrollCopyCount > 1
      ? Array.from({ length: autoScrollCopyCount }, () => items).flat()
      : items;
  const {
    pauseFor: pauseAutoScroll,
    scrollByViewport: scrollAutoByViewport,
  } = useContinuousHorizontalScroll({
    railRef,
    enabled: autoScroll && !isLoading && !errorText,
    itemCount: items.length,
  });

  const scrollByPage = (direction) => {
    if (!railRef.current) return;
    if (autoScroll && items.length > 1 && scrollAutoByViewport(direction)) return;
    if (autoScroll && items.length > 1) pauseAutoScroll();
    railRef.current.scrollBy({
      left: railRef.current.clientWidth * direction,
      behavior: "smooth",
    });
  };

  return (
    <section className={`backend-section backend-section-${section.id}`}>
      <div className="backend-section-header">
        <div className="backend-section-title">
          <section.icon className="backend-section-icon" />
          <div>
            <div className="backend-section-name">{section.title}</div>
          </div>
        </div>

        <div className="backend-section-actions">
          {section.sectionUrl && (
            <Link to={section.sectionUrl} className="view-all-link">
              전체보기
            </Link>
          )}
          <button type="button" onClick={() => scrollByPage(-1)} aria-label={`${section.title} 이전`}>
            ‹
          </button>
          <button type="button" onClick={() => scrollByPage(1)} aria-label={`${section.title} 다음`}>
            ›
          </button>
        </div>
      </div>

      {errorText ? <div className="section-state error">{errorText}</div> : null}

      {!errorText && isLoading ? (
        <div
          className="backend-card-rail is-skeleton-rail"
          aria-hidden="true"
          aria-label={`${section.title} 목록`}
          role="region"
        >
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <article className="backend-card skeleton-card" key={`${section.id}-skeleton-${index}`}>
              <span className="skeleton-title" />
            </article>
          ))}
        </div>
      ) : !errorText && items.length === 0 ? (
        renderEmpty ? (
          <div className="backend-card-rail" aria-label={`${section.title} 목록`} role="region">
            {renderEmpty()}
          </div>
        ) : (
          <div className="section-state">{emptyText}</div>
        )
      ) : (
        <div
          className={`backend-card-rail${autoScroll ? " is-auto-rail" : ""}`}
          ref={railRef}
          aria-label={`${section.title} 목록`}
          role="region"
        >
          {railItems.map((item, index) => {
            const sourceIndex = autoScroll && items.length ? index % items.length : index;
            const isDuplicate = autoScroll && index >= items.length;
            const card = renderCard(item, sourceIndex, index, isDuplicate);
            if (!React.isValidElement(card)) return card;

            return React.cloneElement(card, {
              key: `${section.id}-rail-${index}-${item?.id || item?.vendor_name || sourceIndex}`,
            });
          })}
        </div>
      )}
    </section>
  );
};

const VendorCard = ({ vendor, section, index, isFavorite, onFavoriteToggle, onLaunch }) => {
  const imageCandidates = useMemo(() => getVendorImages(vendor), [vendor]);
  const [imageIndex, setImageIndex] = useState(0);
  const image = imageCandidates[imageIndex] || "";
  const title = getVendorTitle(vendor);
  const href = section.getCardUrl(vendor);
  const canLaunch = Boolean(section.launchMode) && !href;
  const content = (
    <article className={`backend-card vendor-card ${image ? "has-image" : "no-image"}`}>
      {image ? (
        <img
          src={image}
          alt={title}
          loading={index < 4 ? "eager" : "lazy"}
          onError={() => setImageIndex((current) => current + 1)}
        />
      ) : null}
      <div className="backend-card-title">
        <strong>{title}</strong>
      </div>
      <div className="backend-card-hover-layer">
        <span
          className={`backend-card-play${href || canLaunch ? "" : " is-disabled"}`}
          aria-hidden="true"
        />
        <strong>{title}</strong>
      </div>
    </article>
  );

  if (canLaunch) {
    return (
      <div className="backend-card-shell">
        <button
          type="button"
          className="backend-card-link backend-card-button"
          onClick={() => onLaunch?.(vendor, title)}
          aria-label={`${title} 실행`}
        >
          {content}
        </button>
        <FavoriteToggleButton
          active={isFavorite}
          label={title}
          onToggle={() => onFavoriteToggle?.(vendor, title)}
        />
      </div>
    );
  }

  if (!href) {
    return (
      <div className="backend-card-shell">
        {content}
        <FavoriteToggleButton
          active={isFavorite}
          label={title}
          onToggle={() => onFavoriteToggle?.(vendor, title)}
        />
      </div>
    );
  }

  return (
    <div className="backend-card-shell">
      <Link to={href} className="backend-card-link">
        {content}
      </Link>
      <FavoriteToggleButton
        active={isFavorite}
        label={title}
        onToggle={() => onFavoriteToggle?.(vendor, title)}
      />
    </div>
  );
};

const EventCard = ({ event, index, isDuplicate = false }) => {
  const imageCandidates = useMemo(
    () => event?.images || (event?.image ? [event.image] : []),
    [event]
  );
  const [imageIndex, setImageIndex] = useState(0);
  const image = imageCandidates[imageIndex] || "";
  const content = (
    <article className={`backend-card event-card ${image ? "has-image" : "no-image"}`}>
      {image ? (
        <img
          src={image}
          alt={event.title}
          loading={index < 3 ? "eager" : "lazy"}
          onError={() => setImageIndex((current) => current + 1)}
        />
      ) : null}
      <div className="backend-card-title">
        <strong>{event.title}</strong>
      </div>
      <div className="backend-card-hover-layer event-hover-layer">
        <span className="event-card-action" aria-hidden="true">
          이벤트 보기
        </span>
        <strong>{event.title}</strong>
      </div>
    </article>
  );

  return (
    <Link
      to={event.href}
      className="backend-card-link"
      aria-hidden={isDuplicate || undefined}
      tabIndex={isDuplicate ? -1 : undefined}
    >
      {content}
    </Link>
  );
};

const EmptyEventCard = ({ text }) => (
  <article className="backend-card event-card no-image event-empty-card">
    <div className="backend-card-title">
      <strong>{text}</strong>
    </div>
  </article>
);

const HomeBackendSections = () => {
  const { user } = useAuth();
  const [vendorsByType, setVendorsByType] = useState({});
  const [events, setEvents] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [favoriteKeys, setFavoriteKeys] = useState(() => new Set());
  const accountKey = useMemo(() => getRecentPlayAccountKey(user), [user]);

  useEffect(() => {
    let isMounted = true;

    const loadVendorSection = async (section) => {
      try {
        const response = await siteApi.getGameVendors({
          game_type: section.gameType,
          order_by_name: "sequence",
          order_by_type: "ASC",
          limit: "ALL",
        });

        if (!isMounted) return;

        if (response.success) {
          setVendorsByType((current) => ({
            ...current,
            [section.gameType]: getRows(response),
          }));
          return;
        }

        setErrors((current) => ({
          ...current,
          [section.id]: response.error || "벤더 정보를 불러오지 못했습니다.",
        }));
      } catch (error) {
        if (!isMounted) return;
        setErrors((current) => ({
          ...current,
          [section.id]: "벤더 정보를 불러오지 못했습니다.",
        }));
      }
    };

    const loadEvents = async () => {
      try {
        const response = await siteApi.getEvents({
          limit: 12,
          page: 1,
          order_by_name: "sequence",
          order_by_type: "ASC",
        });

        if (!isMounted) return;

        if (response.success) {
          setEvents(getRows(response).map(normalizeEventCard));
          return;
        }

        setErrors((current) => ({
          ...current,
          events: response.error || "이벤트를 불러오지 못했습니다.",
        }));
      } catch (error) {
        if (!isMounted) return;
        setErrors((current) => ({
          ...current,
          events: "이벤트를 불러오지 못했습니다.",
        }));
      }
    };

    const loadAll = async () => {
      setIsLoading(true);
      await Promise.all([...VENDOR_SECTIONS.map(loadVendorSection), loadEvents()]);
      if (isMounted) setIsLoading(false);
    };

    loadAll();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!accountKey) {
      setFavoriteKeys(new Set());
      return undefined;
    }

    const refreshFavorites = () => {
      setFavoriteKeys(new Set(getFavoriteGames(accountKey).map((item) => item.key)));
    };

    refreshFavorites();

    const handleFavoritesUpdated = (event) => {
      if (!event?.detail?.accountKey || event.detail.accountKey === accountKey) {
        refreshFavorites();
      }
    };

    const handleStorage = (event) => {
      if (!event.key || event.key.endsWith(`:${accountKey}`)) {
        refreshFavorites();
      }
    };

    window.addEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [accountKey]);

  const sections = useMemo(
    () =>
      VENDOR_SECTIONS.map((section) => ({
        ...section,
        items: vendorsByType[section.gameType] || [],
      })),
    [vendorsByType]
  );

  const handleVendorLaunch = (vendor, label, section) => {
    if (section?.launchMode === "holdem") {
      launchHoldemGame(vendor, {
        label,
        gameType: section.gameType,
        meta: section.title,
      });
      return;
    }

    launchProviderLobby(vendor, {
      label,
      gameType: section?.gameType || "live-casino",
      meta: section?.title,
    });
  };

  const buildFavoriteItem = (vendor, label, section) => {
    const href = section.getCardUrl?.(vendor) || "";
    return createFavoriteProviderItem(vendor, {
      label,
      gameType: section.gameType,
      meta: section.title,
      href,
      kind: section.launchMode === "holdem" ? "holdem-provider" : href ? "slot-provider" : "provider",
    });
  };

  const handleFavoriteToggle = (vendor, label, section) => {
    if (!user?.isAuthenticated || !accountKey) {
      dispatchAuthRequired();
      return;
    }

    const result = toggleFavoriteGame(accountKey, buildFavoriteItem(vendor, label, section));
    setFavoriteKeys(new Set(result.rows.map((item) => item.key)));
  };

  return (
    <StyledHomeBackendSections>
      {sections.map((section) => (
        <SectionRail
          key={section.id}
          section={section}
          items={section.items}
          isLoading={isLoading}
          emptyText={`${section.title} 벤더가 없습니다.`}
          errorText={errors[section.id]}
          renderCard={(vendor, index) => (
            <VendorCard
              key={`${section.id}-${vendor?.id || vendor?.vendor_name || index}`}
              vendor={vendor}
              section={section}
              index={index}
              isFavorite={favoriteKeys.has(
                buildFavoriteItem(vendor, getVendorTitle(vendor), section).key
              )}
              onFavoriteToggle={(targetVendor, label) =>
                handleFavoriteToggle(targetVendor, label, section)
              }
              onLaunch={(targetVendor, label) => handleVendorLaunch(targetVendor, label, section)}
            />
          )}
        />
      ))}

      <SectionRail
        section={EVENT_SECTION}
        items={events}
        isLoading={isLoading}
        skeletonCount={4}
        emptyText="진행 중인 이벤트가 없습니다."
        errorText={errors.events}
        renderEmpty={() => <EmptyEventCard text="진행 중인 이벤트가 없습니다." />}
        autoScroll
        renderCard={(event, index, _railIndex, isDuplicate) => (
          <EventCard
            key={`event-${event.id || index}`}
            event={event}
            index={index}
            isDuplicate={isDuplicate}
          />
        )}
      />
    </StyledHomeBackendSections>
  );
};

export default HomeBackendSections;
